import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  ChecklistTemplateOption,
  checklistItemsForSave,
  normalizeChecklistItems,
} from '../lib/checklistTemplates'

interface UseChecklistTemplatesResult {
  templates: ChecklistTemplateOption[]
  loading: boolean
  refresh: () => Promise<void>
  saveTemplate: (input: {
    id?: string
    name: string
    description?: string
    service_type?: string | null
    items: Array<{ label: string; sort_order?: number }>
    is_active?: boolean
  }) => Promise<{ ok: boolean; error?: string }>
  deleteTemplate: (id: string) => Promise<{ ok: boolean; error?: string }>
}

function mapDbRow(row: any): ChecklistTemplateOption {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    service_type: row.service_type,
    items: normalizeChecklistItems(row.items),
    is_system: !!row.is_system,
    source: 'saved',
  }
}

export function useChecklistTemplates(): UseChecklistTemplatesResult {
  const [templates, setTemplates] = useState<ChecklistTemplateOption[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('checklist_templates')
        .select('id, name, description, service_type, items, is_active, is_system')
        .eq('is_active', true)
        .order('name', { ascending: true })
      if (error) throw error
      setTemplates((data || []).map(mapDbRow))
    } catch (e) {
      console.warn('Failed to load checklist templates (migration may be pending):', e)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const saveTemplate = useCallback(
    async (input: {
      id?: string
      name: string
      description?: string
      service_type?: string | null
      items: Array<{ label: string; sort_order?: number }>
      is_active?: boolean
    }) => {
      const trimmed = input.name.trim()
      if (!trimmed) return { ok: false, error: 'Template name is required.' }
      const items = checklistItemsForSave(input.items)
      if (items.length === 0) {
        return { ok: false, error: 'Add at least one checklist item.' }
      }

      const payload = {
        name: trimmed,
        description: input.description?.trim() || null,
        service_type: input.service_type || null,
        items,
        is_active: input.is_active !== false,
        updated_at: new Date().toISOString(),
      }

      try {
        if (input.id) {
          const { error } = await supabase
            .from('checklist_templates')
            .update(payload)
            .eq('id', input.id)
          if (error) throw error
        } else {
          const { error } = await supabase.from('checklist_templates').insert({
            ...payload,
            is_system: false,
          })
          if (error) throw error
        }
        await refresh()
        return { ok: true }
      } catch (e: any) {
        return { ok: false, error: e?.message || 'Failed to save template.' }
      }
    },
    [refresh]
  )

  const deleteTemplate = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase.from('checklist_templates').delete().eq('id', id)
        if (error) throw error
        await refresh()
        return { ok: true }
      } catch (e: any) {
        return { ok: false, error: e?.message || 'Failed to delete template.' }
      }
    },
    [refresh]
  )

  return { templates, loading, refresh, saveTemplate, deleteTemplate }
}
